import axios, { etsFetch, getBaseUrl } from "../utils/axios";
import JSZip from "jszip";
import localStorageHandler from "./LocalstorageHandler";
import Logger from "../Logger";
import { getMaxUiMode, isUiModeDeveloper } from "../uiMode";

import { decodeFromCP437, encodeToCP437 } from "../utils/UmlautEncodeDecode";
import universalstorageHandler from "./UniversalstorageHandler";
import { AxiosResponse } from "axios";
import { cleanDecode, cleanEncode, downloadFile, PRODUCT_PRIORITY_TYPES } from "../utils/CommonUtils";
import { etsWebSocket } from "../etsFunctions/etsWebSocket";
import { showToast, updateToastProgress } from "../components/Toasts";
import KnxProductInfo from "../models/KnxProductInfo";
import { itx } from "../translationItx/itx";
import type { ScriptWizardEvidence, ScriptWizardEvidenceResponse } from "../scriptwizard/evidence";

const logger = new Logger();
const voltus_cache_key = "voltus_rows"; // no itx
const voltus_cache_ttl_ms = 7 * 24 * 60 * 60 * 1000; // 7 days
const minute = 60 * 1000;
const image_cache_revalidate_after_ms = 60 * minute;

function apiErrorMessage(error: any, fallback: string): string {
  if (typeof error === "string" && error.trim() && !/<(?:!doctype|html|body)\b/i.test(error)) return error;
  const message = error?.MESSAGE || error?.message || error?.error;
  return typeof message === "string" && message.trim() ? message : fallback;
}

function normalizeMetadataForUpload(metadata: any): Record<string, string> {
  return Object.fromEntries(
    Object.entries(metadata || {}).map(([key, value]) => {
      if (value === undefined || value === null) return [key, ""];
      if (typeof value === "string") return [key, value];
      if (typeof value === "object") {
        try {
          return [key, JSON.stringify(value)];
        } catch {
          return [key, String(value)];
        }
      }
      return [key, String(value)];
    }),
  );
}

export type DownloadImageDataSource = string;

export type DownloadImageDataResult = {
  image?: string;
  source?: DownloadImageDataSource;
  imageUrl: string;
};

export type LoadFrontImageParameters = {
  orderNumber: string;
  catalogId: string;
  articleNo?: string;
  onImageSource?: (source: string, detail: string) => void;
};

export type CachedImageDataEntry = {
  id?: string;
  blob?: ArrayBuffer;
  etag?: string;
  lastModified?: string;
  imageSource?: string;
  timestamp?: number;
};

type CachedImageDataEntryWithBlob = CachedImageDataEntry & { blob: ArrayBuffer };

export type StoredImageInfo = {
  Id: string;
  Name: string;
  lastModified?: string;
  metadata?: any;
};

export type StoreId = string;

export type ProductConfigHistoryItem = {
  id: string;
  productId: string;
  snapshot: string;
  version?: number;
  createdAt?: string;
  createdBy?: string;
  reason?: string;
  label?: string;
  keep?: boolean;
  lastModified?: string;
  contentLength?: number;
  metadata?: Record<string, any>;
};

export type ProductConfigGitlabSyncStatus = {
  productId: string;
  configBlobId?: string;
  status: "disabled" | "pending" | "syncing" | "synced" | "failed";
  commitUrl?: string;
  lastError?: string;
  lastAttemptAt?: string;
  syncedAt?: string;
};

export type ProductConfigGitlabBulkSyncStatus = {
  status: "disabled" | "pending" | "syncing" | "synced" | "failed";
  jobId?: string;
  productCount: number;
  actionCount?: number;
  commitUrl?: string;
  lastError?: string;
  startedAt?: string;
  syncedAt?: string;
  completedAt?: string;
};

export type ProductConfigGitlabFileUploadStatus = {
  status: "disabled" | "synced" | "failed";
  filePath?: string;
  commitUrl?: string;
  lastError?: string;
  syncedAt?: string;
};

export type ProductConfigGitlabOrderFolderAssets = {
  status: "disabled" | "synced" | "failed";
  manufacturer: string;
  orderNumber: string;
  configId: string;
  folderPath?: string;
  configFolderPath?: string;
  allSignals?: {
    fileName: string;
    filePath: string;
  };
  applicationXml?: {
    fileName: string;
    filePath: string;
  };
  scripts?: Array<{
    fileName: string;
    filePath: string;
    directory?: "scripts";
  }>;
  pdfs: Array<{
    fileName: string;
    filePath: string;
  }>;
  lastError?: string;
  checkedAt?: string;
};

export type VoltusCachedRow = {
  Id: number;
  EAN: string;
  Manufacturer: string;
  ArticleNo: string;
  Description: string;
  ShopId: string;
  Page: string;
  Image: string;
  Price: string;
  Availibility: string;
  Variant: string;
  Color: string;
  Size: string;
  ProductType: string;
  DetailedDescription: string;
  ShipmentCost: string;
  Condition: string;
  GoogleProductCategory: string;
  EnergyEfficiencyClass: string;
  MinimalEnergyClass: string;
  MaximalEnergyClass: string;
  UnitPricingMeasure: string;
  UnitPricingBaseMeasure: string;
  AdditionalInfo: string;
};

function parseVoltusDatafeedLine(line: string): string[] {
  let rawSplit = line.split(/"\t"/g).map((field) => field.replace(/^"+|"+$/g, "").trim());
  if (line.startsWith('"Professional')) {
    rawSplit = ["", ...rawSplit];
  }
  return rawSplit;
}

function transformVoltusDatafeedLine(fields: string[], Id: number): VoltusCachedRow | null {
  if (fields.length < 19) return null;

  const rawTitle = fields[1]?.trim() || "";
  const titleSegments = rawTitle.split(" | ");
  const ProductName = titleSegments[0]?.trim() || "";
  const Variant = titleSegments.length > 0 ? titleSegments.slice(1).join(" | ") : "";
  const Color = fields[12]?.trim() || "";
  const Size = "";

  const productNameParts = ProductName.split(" ");
  const Manufacturer = fields[10]?.trim() || productNameParts[0] || "";
  const ArticleNo = fields[11]?.trim() || productNameParts[1] || "";
  let Description = ProductName;
  const descPrefix = `${Manufacturer} ${ArticleNo}`.trim();
  if (descPrefix && Description.toLowerCase().startsWith(descPrefix.toLowerCase())) {
    Description = Description.slice(descPrefix.length).trim();
  }
  if (Variant) Description = `${Description} | ${Variant}`;

  const UnitPricingBaseMeasureRaw = fields[20]?.trim() || "";
  return {
    Id,
    EAN: fields[9]?.trim() || "",
    Manufacturer,
    ArticleNo,
    Description,
    ShopId: fields[0]?.trim() || "",
    Page: fields[3]?.trim() || "",
    Image: fields[5]?.trim() || "",
    Price: fields[2]?.trim() || "",
    Availibility: fields[8]?.trim() || "",
    Variant,
    Color,
    Size,
    ProductType: fields[4]?.trim() || "",
    DetailedDescription: fields[6]?.trim() || "",
    ShipmentCost: fields[7]?.trim() || "",
    Condition: fields[13]?.trim() || "",
    GoogleProductCategory: fields[14]?.trim() || "",
    EnergyEfficiencyClass: fields[16]?.trim() || "",
    MinimalEnergyClass: fields[17]?.trim() || "",
    MaximalEnergyClass: fields[18]?.trim() || "",
    UnitPricingMeasure: fields[19]?.trim() || "",
    UnitPricingBaseMeasure: UnitPricingBaseMeasureRaw.endsWith('"')
      ? UnitPricingBaseMeasureRaw.slice(0, -1)
      : UnitPricingBaseMeasureRaw,
    AdditionalInfo: fields[15]?.trim() || "",
  };
}

function normalizeVoltusSearchText(value?: string): string {
  return cleanDecode(String(value || ""))
    .replace(/\s+/g, "")
    .toLowerCase();
}

function filterVoltusRows(
  rows: VoltusCachedRow[],
  search: string,
  manufacturer?: string,
  limit = 40,
): VoltusCachedRow[] {
  const searchText = normalizeVoltusSearchText(search);
  const manufacturerText = normalizeVoltusSearchText(manufacturer);
  const scoredRows: Array<{ row: VoltusCachedRow; score: number }> = [];

  for (const row of rows) {
    const articleNo = normalizeVoltusSearchText(row.ArticleNo);
    const rowManufacturer = normalizeVoltusSearchText(row.Manufacturer);

    if (
      manufacturerText &&
      rowManufacturer &&
      !rowManufacturer.includes(manufacturerText) &&
      !manufacturerText.includes(rowManufacturer)
    ) {
      continue;
    }

    let score = 0;
    if (searchText) {
      if (articleNo === searchText) score += 100;
      else if (articleNo.startsWith(searchText)) score += 70;
      else if (articleNo.includes(searchText)) score += 45;
      else if (
        [
          row.EAN,
          row.Description,
          row.Variant,
          row.ShopId,
          row.ProductType,
          row.DetailedDescription,
          row.AdditionalInfo,
        ].some((value) => normalizeVoltusSearchText(value).includes(searchText))
      ) {
        score += 20;
      } else {
        continue;
      }
    } else {
      score += 10;
    }

    if (manufacturerText && rowManufacturer === manufacturerText) score += 15;
    scoredRows.push({ row, score });
  }

  return scoredRows
    .sort((a, b) => b.score - a.score || a.row.ArticleNo.localeCompare(b.row.ArticleNo))
    .slice(0, limit)
    .map((entry) => entry.row);
}

export default class ProductStorageHandler {
  private static imageDownloadRequests = new Map<string, Promise<DownloadImageDataResult>>();
  static async getCachedVoltusRows(): Promise<VoltusCachedRow[] | null> {
    try {
      const rows = await localStorageHandler().loadVoltusData(voltus_cache_key, voltus_cache_ttl_ms);
      return Array.isArray(rows) && rows.length > 0 ? rows : null;
    } catch {
      return null;
    }
  }

  static async setCachedVoltusRows(rows: VoltusCachedRow[]): Promise<boolean> {
    try {
      return await localStorageHandler().saveVoltusData(voltus_cache_key, rows);
    } catch {
      return false;
    }
  }

  static async clearCachedVoltusRows(): Promise<boolean> {
    try {
      return await localStorageHandler().deleteVoltusData(voltus_cache_key);
    } catch {
      return false;
    }
  }

  static async ensureCachedVoltusRows(): Promise<VoltusCachedRow[] | null> {
    const cachedRows = await ProductStorageHandler.getCachedVoltusRows();
    if (cachedRows?.length) return cachedRows;

    const csvText = await ProductStorageHandler.downloadStoreData<string>("voltus");
    if (!csvText) return null;

    const processedRows = csvText
      .split("\n")
      .filter(Boolean)
      .map((line, index) => transformVoltusDatafeedLine(parseVoltusDatafeedLine(line), index))
      .filter((row): row is VoltusCachedRow => row !== null);

    if (processedRows.length > 0) {
      await ProductStorageHandler.setCachedVoltusRows(processedRows);
    }
    return processedRows.length > 0 ? processedRows : null;
  }

  static async lookupVoltusByArticleNo(articleNo: string): Promise<VoltusCachedRow | null> {
    if (!articleNo) return null;
    return (await ProductStorageHandler.downloadStoreData<VoltusCachedRow>("voltus", articleNo)) ?? null;
  }

  static async searchVoltusRows(search: string, manufacturer?: string, limit = 40): Promise<VoltusCachedRow[]> {
    const cachedRows = await ProductStorageHandler.ensureCachedVoltusRows();
    return cachedRows ? filterVoltusRows(cachedRows, search, manufacturer, limit) : [];
  }

  static async downloadStoreData<T = any>(
    storeId: StoreId,
    articleNumber?: string,
    onDownloadProgress?: (progressEvent: any) => void,
  ): Promise<T | undefined> {
    try {
      if (storeId.toLowerCase() === "voltus") {
        const response = articleNumber
          ? await axios.get(`/api/lookupVoltusDatafeedRow?articleNo=${encodeURIComponent(articleNumber)}`, {
              headers: {
                "Cache-Control": "no-cache", // no itx
              },
            })
          : await axios.get<string>("/api/downloadVoltusDatafeedCSV", {
              responseType: "text",
              headers: {
                "Cache-Control": "no-cache", // no itx
              },
              onDownloadProgress,
            });

        if (response.status == 200) {
          return (response.data?.DATA ?? response.data) as T;
        }
      }
      logger.error(`Unsupported store id: ${storeId}`);
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async uploadProductData(
    applicationProgramId: string,
    knxDataXmlString: string,
    metadata: any,
    onUploadProgress?: (progressEvent: any) => void,
  ): Promise<boolean> {
    const zip = new JSZip();
    zip.file("data", knxDataXmlString);

    const blob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE", // no itx
      compressionOptions: {
        level: 9,
      },
      encodeFileName: (data) => encodeToCP437(data),
    });

    const data = new FormData();
    data.set("data", blob, "data");
    // Azure blob metadata is string-only. Product metadata can contain booleans
    // and numbers (for example Released: false), so normalize only the upload
    // representation and keep the original object for the local cache.
    const uploadMetadata = normalizeMetadataForUpload(metadata);
    data.set("metadata", JSON.stringify(uploadMetadata));

    const uploadResponse = await axios.post(`/api/uploadProductData/${cleanEncode(applicationProgramId)}`, data, {
      timeout: 300000, // Increasing Upload timeout, as this may take minutes to complete for large product files.
      onUploadProgress,
    });

    const success = uploadResponse.status == 200;
    if (success) {
      await ProductStorageHandler.upsertCachedAllMetadata(metadata);
      ProductStorageHandler.markAllMetadataCacheStale();
    }
    return success;
  }

  static async uploadImageData(imageId: string, image: any, metadata: any): Promise<boolean> {
    const data = new FormData();
    data.set("data", image, "data");
    data.set("metadata", JSON.stringify(metadata));

    try {
      const uploadResponse = await axios.post(`/api/uploadImage/${cleanEncode(imageId)}`, data);
      const success = uploadResponse.status == 200 && uploadResponse.data?.STATUS !== "FAILURE";
      if (success) {
        await ProductStorageHandler.deleteCachedImageData(imageId);
        const linkedImageId = ProductStorageHandler.getLinkedWildcardImageId(imageId);
        if (linkedImageId && linkedImageId !== imageId) {
          await ProductStorageHandler.deleteCachedImageData(linkedImageId);
        }
      }
      return success;
    } catch (ex) {
      logger.error(ex);
      return false;
    }
  }

  static async cacheImageData(imageId: string, image: Blob | ArrayBuffer, metadata?: any): Promise<boolean> {
    if (!imageId || !image) return false;

    const ids = new Set([imageId, cleanEncode(imageId), cleanDecode(imageId)].filter(Boolean));
    const imageData = image instanceof Blob ? await image.arrayBuffer() : image;
    const cacheMetadata = {
      ...(metadata || {}),
      imageSource: metadata?.imageSource || "local-knxprod-baggage", // no itx
    };

    const results = await Promise.all(
      Array.from(ids).map((id) => localStorageHandler().saveImageData(id, imageData, cacheMetadata)),
    );
    return results.some(Boolean);
  }

  static async deleteCachedImageData(imageId: string): Promise<boolean> {
    if (!imageId) return false;
    const ids = new Set([imageId, cleanEncode(imageId), cleanDecode(imageId)].filter(Boolean));
    const results = await Promise.all(Array.from(ids).map((id) => localStorageHandler().deleteImageData(id)));
    return results.some(Boolean);
  }

  private static getLinkedWildcardImageId(imageId: string): string {
    if (
      (imageId.startsWith("BE-") || imageId.startsWith("RF-") || imageId.startsWith("SCN-")) &&
      imageId.split("-").length > 1 &&
      (imageId.split("-")[1].endsWith("S") || imageId.split("-")[1].endsWith("W"))
    ) {
      const idSegments = imageId.split("-");
      idSegments[1] = idSegments[1].replace(/.$/, "x");
      return idSegments.join("-");
    }
    return "";
  }

  static async deleteCachedProductData(catalogId: string): Promise<boolean> {
    const ok1 = await localStorageHandler().deleteProdData("O-" + catalogId);
    const ok2 = await localStorageHandler().deleteProdData("CO-" + catalogId);
    return ok1;
  }

  static async loadCachedProductDataAsObject(catalogId: string): Promise<any | undefined> {
    const cachedProductData = await localStorageHandler().loadProdData("O-" + catalogId);
    return cachedProductData;
  }

  static async storeCachedProductDataAsObject(catalogId: string, data: any): Promise<boolean> {
    const ok = await localStorageHandler().saveProdData("O-" + catalogId, data);
    return ok;
  }

  // static async loadCachedMetaDataAsObject(catalogId: string): Promise<any | undefined> {
  //   const cachedMetaData = await localStorageHandler().loadMetaData("O-" + catalogId);
  //   return cachedMetaData;
  // }

  // static async storeCachedMetaDataAsObject(catalogId: string, data: any): Promise<boolean> {
  //   const ok = await localStorageHandler().saveMetaData("O-" + catalogId, data);
  //   return ok;
  // }

  static async uploadProductConfig(
    applicationProgramId: string,
    knxDataXmlString: string,
    metadata: any,
  ): Promise<boolean> {
    //
    // MWE:
    // since version 3.3 28.5.2025 we upload the config with a different name.
    // this is to avoid conflicts with older versions of Estada which still use the old config name.
    //
    const user = state.loggedInUser;
    const historyMetadata = {
      ...(metadata || {}),
      HistoryReason: metadata?.HistoryReason || metadata?.historyreason || "save",
      HistoryCreatedBy:
        metadata?.HistoryCreatedBy || metadata?.historycreatedby || user?.email || user?.name || user?.id || "",
    };

    return await this.uploadProductData("config-2-" + applicationProgramId, knxDataXmlString, historyMetadata); // no itx
  }

  static async downloadProductConfig(catalogId: string): Promise<string | undefined> {
    const force = `?${new Date().getTime()}`;

    //
    // try to download the new config first.
    //
    let res = await this.downloadProductData("config-2-" + catalogId);
    if (!res) {
      //
      // if not found, try to download the old config.
      //
      res = await this.downloadProductData("config-" + catalogId);
    }

    return res;
  }

  static async listProductConfigHistory(catalogId: string): Promise<ProductConfigHistoryItem[]> {
    try {
      const response = await axios.get(`/api/listProductConfigHistory/${cleanEncode(catalogId)}`, {
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });

      if (response.status == 200 && Array.isArray(response.data)) {
        return response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return [];
  }

  static async downloadProductConfigSnapshot(catalogId: string, snapshot: string): Promise<any | undefined> {
    try {
      const response = await axios.get(`/api/getProductConfigSnapshot/${cleanEncode(catalogId)}`, {
        params: { snapshot },
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });

      if (response.status == 200) {
        return response.data?.config;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async restoreProductConfig(catalogId: string, snapshot: string): Promise<boolean> {
    try {
      const user = state.loggedInUser;
      const response = await axios.post(`/api/restoreProductConfig/${cleanEncode(catalogId)}`, {
        snapshot,
        createdBy: user?.email || user?.name || user?.id || "",
      });

      return response.status == 200 && (response.data?.STATUS === "SUCCESS" || response.data?.id);
    } catch (ex) {
      logger.error(ex);
    }

    return false;
  }

  static async getProductConfigGitlabSyncStatus(catalogId: string): Promise<ProductConfigGitlabSyncStatus | undefined> {
    try {
      const response = await axios.get(`/api/productConfigGitlabSyncStatus/${cleanEncode(catalogId)}`, {
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async syncProductConfigToGitlab(
    catalogId: string,
    reason = "manual-sync",
  ): Promise<ProductConfigGitlabSyncStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const response = await axios.post(`/api/syncProductConfigToGitlab/${cleanEncode(catalogId)}`, {
        reason,
        createdBy: user?.email || user?.name || user?.id || "",
      });

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async syncAllProductConfigsToGitlab(
    reason = "complete-config-sync",
  ): Promise<ProductConfigGitlabBulkSyncStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const response = await axios.post(`/api/syncAllProductConfigsToGitlab`, {
        reason,
        createdBy: user?.email || user?.name || user?.id || "",
      });

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async getProductConfigGitlabBulkSyncStatus(
    jobId: string,
  ): Promise<ProductConfigGitlabBulkSyncStatus | undefined> {
    try {
      const response = await axios.get(`/api/productConfigGitlabBulkSyncStatus/${cleanEncode(jobId)}`, {
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async uploadAllSignalsToGitlab(params: {
    manufacturer: string;
    orderNumber: string;
    configId: string;
    csvContent: string;
    fileName?: string;
    onUploadProgress?: (progressEvent: any) => void;
  }): Promise<ProductConfigGitlabFileUploadStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const { onUploadProgress, ...requestParams } = params;
      const response = await axios.post(
        `/api/uploadAllSignalsToGitlab`,
        {
          ...requestParams,
          createdBy: user?.email || user?.name || user?.id || "",
        },
        { onUploadProgress },
      );

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async uploadConfigTextFileToGitlab(params: {
    manufacturer: string;
    orderNumber: string;
    configId: string;
    fileName: string;
    content: string;
  }): Promise<ProductConfigGitlabFileUploadStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const response = await axios.post(`/api/uploadConfigTextFileToGitlab`, {
        ...params,
        createdBy: user?.email || user?.name || user?.id || "",
      });
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
    }
    return undefined;
  }

  static async uploadOrderFileToGitlab(params: {
    manufacturer: string;
    orderNumber: string;
    file: File;
    fileName?: string;
    onUploadProgress?: (progressEvent: any) => void;
  }): Promise<ProductConfigGitlabFileUploadStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const formData = new FormData();
      formData.append("manufacturer", params.manufacturer);
      formData.append("orderNumber", params.orderNumber);
      formData.append("createdBy", user?.email || user?.name || user?.id || "");
      if (params.fileName) formData.append("fileName", params.fileName);
      formData.append("file", params.file, params.file.name);

      const response = await axios.post(`/api/uploadOrderFileToGitlab`, formData, {
        onUploadProgress: params.onUploadProgress,
      });

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async uploadConfigScriptToGitlab(params: {
    manufacturer: string;
    orderNumber: string;
    configId: string;
    file: File;
    fileName?: string;
    onUploadProgress?: (progressEvent: any) => void;
  }): Promise<ProductConfigGitlabFileUploadStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const formData = new FormData();
      formData.append("manufacturer", params.manufacturer);
      formData.append("orderNumber", params.orderNumber);
      formData.append("configId", params.configId);
      formData.append("createdBy", user?.email || user?.name || user?.id || "");
      if (params.fileName) formData.append("fileName", params.fileName);
      formData.append("file", params.file, params.file.name);

      const response = await axios.post(`/api/uploadConfigScriptToGitlab`, formData, {
        onUploadProgress: params.onUploadProgress,
      });

      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async uploadConfigApplicationXmlToGitlab(params: {
    manufacturer: string;
    orderNumber: string;
    configId: string;
    catalogId: string;
    file: File;
    onUploadProgress?: (progressEvent: any) => void;
  }): Promise<ProductConfigGitlabFileUploadStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const formData = new FormData();
      formData.append("manufacturer", params.manufacturer);
      formData.append("orderNumber", params.orderNumber);
      formData.append("configId", params.configId);
      formData.append("catalogId", params.catalogId);
      formData.append("createdBy", user?.email || user?.name || user?.id || "");
      formData.append("file", params.file, `${params.catalogId}.xml`);

      const response = await axios.post(`/api/uploadConfigApplicationXmlToGitlab`, formData, {
        onUploadProgress: params.onUploadProgress,
      });

      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async getOrderFolderGitlabAssets(
    manufacturer: string,
    orderNumber: string,
    configId: string,
  ): Promise<ProductConfigGitlabOrderFolderAssets | undefined> {
    try {
      const response = await axios.get(`/api/orderFolderGitlabAssets`, {
        params: { manufacturer, orderNumber, configId },
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });

      if (response.status == 200) {
        return response.data?.DATA || response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async getOrderFolderGitlabFile(
    manufacturer: string,
    orderNumber: string,
    configId: string,
    fileName = "allSignals.csv",
    directory?: "scripts",
  ): Promise<
    { status: string; fileName?: string; filePath?: string; content?: string; lastError?: string } | undefined
  > {
    try {
      const response = await axios.get(`/api/orderFolderGitlabFile`, {
        params: { manufacturer, orderNumber, configId, fileName, directory },
        headers: { "Cache-Control": "no-cache" },
      });
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return { status: "failed", fileName, lastError: apiErrorMessage(ex, "The GitLab file could not be read.") };
    }
    return { status: "failed", fileName, lastError: "The GitLab file could not be read." };
  }

  static async getOrderFolderGitlabBinaryFile(
    manufacturer: string,
    orderNumber: string,
    configId: string,
    fileName: string,
    location: "config" | "order" = "order",
  ): Promise<
    { status: string; fileName?: string; filePath?: string; contentBase64?: string; lastError?: string } | undefined
  > {
    try {
      const response = await axios.get(`/api/orderFolderGitlabBinaryFile`, {
        params: { manufacturer, orderNumber, configId, fileName, location },
        headers: { "Cache-Control": "no-cache" },
      });
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return {
        status: "failed",
        fileName,
        lastError: apiErrorMessage(ex, "The GitLab binary file could not be read."),
      };
    }
    return { status: "failed", fileName, lastError: "The GitLab binary file could not be read." };
  }

  static async discoverScriptWizardDocuments(params: {
    manufacturer: string;
    orderNumber: string;
    articleNos?: string[];
    productName?: string;
  }): Promise<any | undefined> {
    try {
      const response = await axios.post(`/api/scriptWizard/discoverDocuments`, params);
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return {
        status: "failed",
        documents: [],
        lastError: apiErrorMessage(
          ex,
          "The ScriptWizard document-discovery API is unavailable. Deploy the updated KNX backend.",
        ),
      };
    }
    return {
      status: "failed",
      documents: [],
      lastError: "The ScriptWizard document-discovery API is unavailable. Deploy the updated KNX backend.",
    };
  }

  static async collectScriptWizardEvidence(params: {
    manufacturer: string;
    orderNumber: string;
    configId?: string;
    rows: any[];
    applicationXml?: string;
    existingCode?: string;
    knowledge?: Record<string, unknown>;
  }): Promise<ScriptWizardEvidenceResponse> {
    try {
      // Evidence combines GitLab metadata, PDF downloads and repository
      // scripts. Keep this request bounded so the wizard cannot remain in a
      // permanent "pending" state when a remote GitLab operation stalls.
      const response = await axios.post(`/api/scriptWizard/collectEvidence`, params, { timeout: 90000 });
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return {
        status: "failed",
        lastError: apiErrorMessage(ex, "The ScriptWizard evidence API is unavailable. Deploy the updated KNX backend."),
      };
    }
    return {
      status: "failed",
      lastError: "The ScriptWizard evidence API is unavailable. Deploy the updated KNX backend.",
    };
  }

  static async importScriptWizardDocument(params: {
    manufacturer: string;
    orderNumber: string;
    url: string;
    fileName?: string;
    source?: string;
    articleNos?: string[];
    productName?: string;
  }): Promise<ProductConfigGitlabFileUploadStatus | undefined> {
    try {
      const user = state.loggedInUser;
      const response = await axios.post(`/api/scriptWizard/importDocument`, {
        ...params,
        createdBy: user?.email || user?.name || user?.id || "",
      });
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return {
        status: "failed",
        lastError: apiErrorMessage(
          ex,
          "The ScriptWizard document-import API is unavailable. Deploy the updated KNX backend.",
        ),
      };
    }
    return {
      status: "failed",
      lastError: "The ScriptWizard document-import API is unavailable. Deploy the updated KNX backend.",
    };
  }

  static async generateScriptWizardMapSignalToTag(params: {
    manufacturer: string;
    orderNumber: string;
    productName?: string;
    productType?: string;
    productClass?: "sensor" | "button" | "actor" | "unknown";
    rows: any[];
    existingCode?: string;
    inference?: Record<string, unknown>;
    knowledge?: Record<string, unknown>;
    evidence?: ScriptWizardEvidence;
    additionalConsiderations?: string;
    mode?: "claude" | "knowledgebase";
  }): Promise<{ code?: string; source?: string; notes?: string[]; lastError?: string } | undefined> {
    try {
      const response = await axios.post(`/api/scriptWizard/generateMapSignalToTag`, params);
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return { lastError: apiErrorMessage(ex, "The ScriptWizard Claude-generation API is unavailable.") };
    }
    return { lastError: "The ScriptWizard Claude-generation API is unavailable." };
  }

  static async saveScriptWizardLearning(params: {
    manufacturer: string;
    orderNumber: string;
    catalogId: string;
    scriptType?: string;
    originalCode?: string;
    generatedCode: string;
    reviewedCode: string;
    comments: string[];
    learningContext: Record<string, unknown>;
    baseKnowledge: Record<string, unknown>;
  }): Promise<
    | {
        status?: string;
        filePath?: string;
        commitUrl?: string;
        lastError?: string;
        learnedRuleCount?: number;
        tokenUsage?: { model: string; inputTokens: number; outputTokens: number; costUsd?: number };
      }
    | undefined
  > {
    try {
      const response = await axios.post(`/api/scriptWizard/saveLearning`, params);
      if (response.status === 200) return response.data?.DATA || response.data;
    } catch (ex) {
      logger.error(ex);
      return { status: "failed", lastError: apiErrorMessage(ex, "The ScriptWizard learning API is unavailable.") };
    }
    return { status: "failed", lastError: "The ScriptWizard learning API is unavailable." };
  }

  static async downloadProductData(catalogId: string): Promise<string | undefined> {
    try {
      //////////////////
      const response = await axios.get(`/api/downloadProductData/${cleanEncode(catalogId)}`, {
        responseType: "arraybuffer", // no itx
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });

      if (response.status == 200) {
        const blob = new Blob([response.data], {
          type: "application/zip",
        });

        let zip = new JSZip();
        await zip.loadAsync(blob, {
          decodeFileName: (data) => decodeFromCP437(data),
        });
        let dataFile = zip.file("data");

        if (dataFile) {
          const modeldataXmlString = await dataFile.async("string");
          return modeldataXmlString;
        }
      }
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  static async downloadImageZip(imageId: string, fromEts: boolean, zipBase64?: any): Promise<any> {
    // check if imageData is cached

    const cachedImageData = await localStorageHandler().loadImageData(imageId);
    let zip = new JSZip();
    if (cachedImageData) {
      await zip.loadAsync(cachedImageData, {
        decodeFileName: (data) => decodeFromCP437(data),
      });
      return zip.files;
    }
    try {
      let arrayBuffer;

      if (fromEts) {
        arrayBuffer = Uint8Array.from(window.atob(zipBase64), (c) => c.charCodeAt(0)).buffer;
      } else {
        // if imageData not cached, load it from server and save to cache
        const response = await axios.get(`/api/downloadImage/${cleanEncode(imageId)}`, {
          responseType: "arraybuffer", // no itx
        });

        if (response.status == 200) {
          if (response.data.byteLength < 100) {
            let responseString = new TextDecoder().decode(response.data);
            logger.log(responseString);
            if (JSON.parse(responseString)?.STATUS == "FAILURE") {
              return undefined;
            }
          }
          arrayBuffer = response.data;
        }
      }
      await localStorageHandler().saveImageData(imageId, arrayBuffer);
      await zip.loadAsync(arrayBuffer, {
        decodeFileName: (data) => decodeFromCP437(data),
      });
      return zip.files;
    } catch (ex) {
      logger.error(ex);
      return undefined;
    }
  }

  static getImageFileNameFromOrderNumber(orderNumber: string, manufacturerName: string): string {
    let fileName = orderNumber;
    if (manufacturerName.endsWith("Jung")) {
      fileName = fileName.replaceAll(" ", "");
    }
    return fileName;
  }

  private static isCachedImageFresh(entry?: CachedImageDataEntry): entry is CachedImageDataEntryWithBlob {
    const timestamp = Number(entry?.timestamp || 0);
    return Boolean(entry?.blob && timestamp > 0 && Date.now() - timestamp < image_cache_revalidate_after_ms);
  }

  private static getCachedImageHeaders(entry?: CachedImageDataEntry): Record<string, string> {
    const headers: Record<string, string> = {};
    if (entry?.etag) headers["If-None-Match"] = entry.etag; // no itx
    if (entry?.lastModified) headers["If-Modified-Since"] = entry.lastModified; // no itx
    return headers;
  }

  private static getResponseHeader(response: AxiosResponse<any, any>, headerName: string): string | undefined {
    const value = response.headers?.[headerName];
    if (typeof value === "string") {
      return value;
    }
    if (Array.isArray(value)) {
      return value.find((item): item is string => typeof item === "string");
    }
    return undefined;
  }

  private static getImageResponseCacheMetadata(response: AxiosResponse<any, any>) {
    return {
      etag: ProductStorageHandler.getResponseHeader(response, "etag"),
      lastModified: ProductStorageHandler.getResponseHeader(response, "last-modified"),
      imageSource: ProductStorageHandler.getResponseHeader(response, "x-image-source"),
    };
  }

  private static createObjectUrlFromImageData(data: any, contentType = "image/png"): string {
    return URL.createObjectURL(
      new Blob([data], {
        type: contentType,
      }),
    );
  }

  static buildFrontImageUrl(
    knxOrderNumber: string,
    catalogId: string,
    variantArticleNumber: string | undefined,
  ): string {
    const forOldBackend = false; // set to true if you want to use the old backend image download endpoint
    if (forOldBackend) {
      let url = catalogId
        ? `/api/downloadImage/${cleanEncode(knxOrderNumber)}/${cleanEncode(catalogId)}`
        : `/api/downloadImage/${cleanEncode(knxOrderNumber)}`;
      return getBaseUrl() + url;
    }

    let url = `/api/downloadImage2?`;
    url += `knxOrderNumber=${cleanEncode(knxOrderNumber)}`;
    url += `&catalogId=${cleanEncode(catalogId)}`;

    if (variantArticleNumber) {
      url += `&variantArticleNumber=${cleanEncode(variantArticleNumber)}`;
    }

    return getBaseUrl() + url;
  }

  static async downloadImageDataWithSource(
    knxOrderNumber: string,
    catalogId: string,
    variantArticleNumber?: string,
  ): Promise<DownloadImageDataResult> {
    // check if imageData is cached
    const url = ProductStorageHandler.buildFrontImageUrl(knxOrderNumber, catalogId, variantArticleNumber);
    return await ProductStorageHandler.downloadImageDataWithSource1(url);
  }

  static async downloadImageDataWithSource1(imageUrl: string): Promise<DownloadImageDataResult> {
    const existingRequest = ProductStorageHandler.imageDownloadRequests.get(imageUrl);
    if (existingRequest) return existingRequest;

    const download = ProductStorageHandler.executeImageDataDownload(imageUrl);
    ProductStorageHandler.imageDownloadRequests.set(imageUrl, download);
    try {
      return await download;
    } finally {
      if (ProductStorageHandler.imageDownloadRequests.get(imageUrl) === download) {
        ProductStorageHandler.imageDownloadRequests.delete(imageUrl);
      }
    }
  }

  private static async executeImageDataDownload(imageUrl: string): Promise<DownloadImageDataResult> {
    const cachedImageData = (await localStorageHandler().loadImageDataEntry(imageUrl)) as CachedImageDataEntry;
    if (ProductStorageHandler.isCachedImageFresh(cachedImageData)) {
      const objectURL = ProductStorageHandler.createObjectUrlFromImageData(cachedImageData.blob);
      return {
        image: objectURL,
        source: cachedImageData.imageSource ? `browser-cache - ${cachedImageData.imageSource}` : "browser-cache", // no itx
        imageUrl,
      };
    }

    try {
      // if imageData not cached, load it from server and save to cache
      console.log("image not cached, Downloading image data from server:", imageUrl);

      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer", // no itx
        headers: ProductStorageHandler.getCachedImageHeaders(cachedImageData),
        validateStatus: (status) => status === 200 || status === 304,
      });

      if (response.status == 304 && cachedImageData?.blob) {
        //
        // Code 304 == unchanged, so we can use the cached image data.
        // We store it again to update the timestamp
        //
        await localStorageHandler().saveImageData(
          imageUrl,
          cachedImageData.blob,
          ProductStorageHandler.getImageResponseCacheMetadata(response),
        );
        return {
          image: ProductStorageHandler.createObjectUrlFromImageData(cachedImageData.blob),
          source: cachedImageData.imageSource
            ? `browser-cache - ${cachedImageData.imageSource}`
            : ProductStorageHandler.getResponseHeader(response, "x-image-source") || "browser-cache", // no itx
          imageUrl,
        };
      }

      if (response.status == 200) {
        if (response.data.byteLength < 100) {
          let responseString = new TextDecoder().decode(response.data);
          logger.log(responseString);
          let jStatus: any | undefined = undefined;
          try {
            jStatus = JSON.parse(responseString);
          } catch (ex) {
            logger.error(ex);
          }
          //
          // If the backend retuns an error status,
          //  we use the cache the image data
          //
          if (jStatus?.STATUS == "FAILURE") {
            return { imageUrl: imageUrl };
          }
        }

        await localStorageHandler().saveImageData(
          imageUrl,
          response.data,
          ProductStorageHandler.getImageResponseCacheMetadata(response),
        );

        const objectURL = ProductStorageHandler.createObjectUrlFromImageData(
          response.data,
          ProductStorageHandler.getResponseHeader(response, "content-type") || "image/png",
        );

        return {
          image: objectURL,
          source: ProductStorageHandler.getResponseHeader(response, "x-image-source") || "backend", // no itx
          imageUrl: imageUrl,
        };
      }
    } catch (ex) {
      logger.error(ex);
    }
    return { imageUrl: imageUrl };
  }

  static async downloadImageData(
    knxOrderNumber: string,
    catalogId?: string,
    variantArticleNumber?: string,
  ): Promise<string | undefined> {
    const result = await ProductStorageHandler.downloadImageDataWithSource(
      knxOrderNumber,
      catalogId || "",
      variantArticleNumber,
    );
    return result.image;
  }

  static async loadFrontImage({
    orderNumber,
    catalogId,
    articleNo,
    onImageSource,
  }: LoadFrontImageParameters): Promise<string> {
    const result = await ProductStorageHandler.downloadImageDataWithSource(orderNumber, catalogId, articleNo);
    if (!result.image) {
      onImageSource?.("Not found", articleNo ?? orderNumber);
      return "";
    }

    onImageSource?.(result.source || "backend", articleNo ?? orderNumber);
    return result.image;
  }

  static async downloadImageBlob(imageId: string): Promise<Blob | undefined> {
    const cachedImageData = (await localStorageHandler().loadImageDataEntry(imageId)) as CachedImageDataEntry;
    if (ProductStorageHandler.isCachedImageFresh(cachedImageData)) {
      return new Blob([cachedImageData.blob], { type: "image/png" });
    }

    try {
      const response = await axios.get(`/api/downloadImage/${cleanEncode(imageId)}`, {
        responseType: "arraybuffer", // no itx
        headers: ProductStorageHandler.getCachedImageHeaders(cachedImageData),
        validateStatus: (status) => status === 200 || status === 304,
      });

      if (response.status === 304 && cachedImageData?.blob) {
        await localStorageHandler().saveImageData(
          imageId,
          cachedImageData.blob,
          ProductStorageHandler.getImageResponseCacheMetadata(response),
        );
        return new Blob([cachedImageData.blob], { type: "image/png" });
      }

      if (response.status == 200) {
        if (response.data.byteLength < 100) {
          const responseString = new TextDecoder().decode(response.data);
          logger.log(responseString);
          if (JSON.parse(responseString)?.STATUS == "FAILURE") {
            return cachedImageData?.blob
              ? new Blob([cachedImageData.blob], { type: "application/octet-stream" })
              : undefined;
          }
        }

        await localStorageHandler().saveImageData(
          imageId,
          response.data,
          ProductStorageHandler.getImageResponseCacheMetadata(response),
        );
        return new Blob([response.data], {
          type: ProductStorageHandler.getResponseHeader(response, "content-type") || "application/octet-stream",
        });
      }
    } catch (ex) {
      logger.error(ex);
    }
    if (cachedImageData?.blob) {
      return new Blob([cachedImageData.blob], { type: "application/octet-stream" });
    }
    return undefined;
  }

  static async fetchAllImages(): Promise<Array<StoredImageInfo>> {
    try {
      const response = await axios.get(`/api/listImages`);
      if (response.status == 200) {
        let imageList: StoredImageInfo[] = [];
        response.data.forEach((object: any) => {
          const objectMetadata = object?.metadata && typeof object.metadata === "object" ? object.metadata : {};
          const id = cleanDecode(object?.id || "");
          const metadata = {
            ...objectMetadata,
            Id: id,
            Name: objectMetadata.Name ? cleanDecode(objectMetadata.Name) : id,
            lastModified: object?.lastModified,
            metadata: objectMetadata,
          };
          imageList.push(metadata);
        });
        return imageList;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return [];
  }

  static allProductMetadataCache: undefined | Array<any> = undefined;

  static async fetchMetadata2(filter: { manufacturer?: string; productType?: string }): Promise<Array<any>> {
    //
    // for now we fetch all data from the sever
    // and filter it on the client side. This is not optimal, but it works for now.
    //

    let data2: Array<any> = [];
    if (ProductStorageHandler.allProductMetadataCache) {
      data2 = ProductStorageHandler.allProductMetadataCache;
    } else {
      let data: Array<any> = await ProductStorageHandler.fetchAllMetadata("import");
      for (const item of data) {
        if (item.Release == "true") {
          item.ManufacturerName = cleanDecode(item.ManufacturerName || "");
          item.ProductType = cleanDecode(item.ProductType || "");
          item.Description = cleanDecode(item.Description || "");
          item.Variants = cleanDecode(item.Variants || "");
          item.OrderNumber = cleanDecode(item.OrderNumber || "");

          if (item.Variants) {
            try {
              item.Variants = JSON.parse(item.Variants);
              if (Array.isArray(item.Variants) && item.Variants.length > 0) {
                for (let v of item.Variants) {
                  const variantItem = { ...item };

                  variantItem.VariantArticleNumber = v.articleNo;
                  data2.push(variantItem);
                }
              } else {
                data2.push(item);
              }
            } catch (ex) {
              logger.error("Failed to parse Variants JSON for product", item.Id, ex);
              item.Variants = undefined;
            }
          } else {
            data2.push(item);
          }
        }
      }

      ProductStorageHandler.allProductMetadataCache = data2;
    }

    const dataFiltered = data2.filter((item) => {
      if (filter.manufacturer && item.ManufacturerName !== filter.manufacturer) {
        return false;
      }
      if (filter.productType && item.ProductType !== filter.productType) {
        return false;
      }
      return true;
    });

    return dataFiltered;
  }

  static async fetchAllMetadata(
    usage: string,
    onProgress?: (percent: number) => void,
    suppressToast?: boolean,
  ): Promise<Array<any>> {
    try {
      const isDeveloper = isUiModeDeveloper();
      const apiUrl = `/api/listProductData${isDeveloper ? `?admin=true` : ``}`;
      const timeout = isDeveloper ? 3 * minute : minute;

      if (!suppressToast) {
        const text = itx("ProductStorageHandle.produktbersichtherunterladen", "Produktübersicht herunterladen");
        await showToast({
          text: text,
          keepVisible: true,
          modal: true,
          progress: true,
        });
      }

      const response = await axios.get(apiUrl, {
        timeout: timeout,
        onDownloadProgress: (progressEvent: any) => {
          try {
            const current = progressEvent.progress * 100; // 70% of the total progress
            if (!suppressToast) updateToastProgress({ progress: current });
          } catch (ex) {}
        },
        // headers: {
        //   "Cache-Control": "no-cache"
        // },
      });

      if (!suppressToast) updateToastProgress({ progress: 100 });

      if (response.status == 200) {
        const etag = ProductStorageHandler.getResponseHeader(response, "etag"); // no itx
        if (etag) {
          localStorage.setItem("AllProductMetadataEtag", etag);
        }
        let productsInfoList: any = [];
        response.data.forEach((object: any, index: number) => {
          const metadata = ProductStorageHandler.normalizeMetadata({
            ...object.metadata,
            Id: object.id,
            ImageExists: object.imageExists,
            lastModified: object.lastModified,
            ContentLength: object.contentLength,
          });
          productsInfoList.push(metadata);
        });
        return productsInfoList;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return [];
  }

  static async fetchFilteredMetadata(filter: string): Promise<Array<any>> {
    try {
      const isDeveloper = isUiModeDeveloper();
      const apiUrl = `/api/listProductDataFiltered/${filter}${isDeveloper ? `?admin=true` : ``}`;
      const timeout = isDeveloper ? 3 * minute : minute;

      const text = itx("ProductStorageHandle.produktbersichtherunterladen", "Produktübersicht herunterladen");
      await showToast({
        text: text,
        keepVisible: true,
        modal: true,
        progress: true,
      });

      const response = await axios.get(apiUrl, {
        timeout: timeout,
        onDownloadProgress: (progressEvent: any) => {
          try {
            const current = progressEvent.progress * 100; // 70% of the total progress
            updateToastProgress({ progress: current });
          } catch (ex) {}
        },
      });

      updateToastProgress({ progress: 100 });

      if (response.status == 200) {
        let productsInfoList: any = [];
        response.data.forEach((object: any, index: number) => {
          const metadata = ProductStorageHandler.normalizeMetadata({
            ...object.metadata,
            Id: object.id,
            ImageExists: object.imageExists,
            lastModified: object.lastModified,
          });
          productsInfoList.push(metadata);
        });
        return productsInfoList;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return [];
  }

  static async fetchFilteredProductIds(filter: string): Promise<Array<any>> {
    try {
      const apiUrl = `/api/listProductIdsFiltered/${cleanEncode(filter)}`;
      const response = await axios.get(apiUrl);
      if (response.status == 200) {
        return response.data;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return [];
  }

  static async validateMetadata(): Promise<Boolean> {
    try {
      const response = await axios.get(`/api/validateProductData`, {
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });
      if (response.status == 200) {
        const etagFromResponse = response.data;
        const cachedEtag = localStorage.getItem("AllProductMetadataEtag");
        return cachedEtag ? String(etagFromResponse).trim() === cachedEtag.trim() : false;
      } else {
        return false;
      }
    } catch (ex) {
      logger.error(ex);
    }
    return false;
  }

  static async loadCachedAllMetadataAsObject(usage: string): Promise<any | undefined> {
    const cachedAllProductMetadata = await localStorageHandler().loadProdData("AllProductMetadata"); // no itx
    return cachedAllProductMetadata;
  }

  static async storeCachedAllMetadataAsObject(usage: string, data: any): Promise<boolean> {
    const ok = await localStorageHandler().saveProdData("AllProductMetadata", data); // no itx
    return ok;
  }

  static markAllMetadataCacheStale() {
    try {
      localStorage.removeItem("AllProductMetadataEtag");
    } catch (ex) {
      logger.error(ex);
    }
  }

  static async upsertCachedAllMetadata(metadata: any): Promise<boolean> {
    try {
      if (!metadata?.CatalogId) return false;

      const cachedAllProductMetadata = await ProductStorageHandler.loadCachedAllMetadataAsObject("all");
      if (!Array.isArray(cachedAllProductMetadata)) return false;

      const normalizedMetadata = ProductStorageHandler.normalizeMetadata(metadata);
      const idx = cachedAllProductMetadata.findIndex((item: any) => item?.CatalogId === normalizedMetadata.CatalogId);
      const updatedMetadata =
        idx >= 0 ? { ...cachedAllProductMetadata[idx], ...normalizedMetadata } : { ...normalizedMetadata };

      const nextCache =
        idx >= 0
          ? cachedAllProductMetadata.map((item: any, itemIndex: number) => (itemIndex === idx ? updatedMetadata : item))
          : [...cachedAllProductMetadata, updatedMetadata];

      const ok = await ProductStorageHandler.storeCachedAllMetadataAsObject("all", nextCache);
      if (ok) ProductStorageHandler.markAllMetadataCacheStale();
      return ok;
    } catch (ex) {
      logger.error(ex);
      return false;
    }
  }

  // 25.09.2025 : If etag matches, load from cache, else fetch from server and update cache
  static async getAllMetadataWithSource(
    usage: string,
    suppressToast?: boolean,
  ): Promise<{ data: Array<any>; fromCache: boolean }> {
    const isProductDataInCacheUptodate = await ProductStorageHandler.validateMetadata();
    let res = await ProductStorageHandler.loadCachedAllMetadataAsObject(usage);

    if (res && isProductDataInCacheUptodate) {
      return { data: res, fromCache: true };
    }

    const cachedRes = res;
    res = await ProductStorageHandler.fetchAllMetadata(usage, undefined, suppressToast);

    if ((!res || res.length === 0) && cachedRes && cachedRes.length > 0) {
      return { data: cachedRes, fromCache: true };
    }

    if (res && res.length > 0) {
      ProductStorageHandler.storeCachedAllMetadataAsObject(usage, res); // can run in background, not await needed
    }

    return { data: res, fromCache: false };
  }

  static async getAllMetadata(usage: string, suppressToast?: boolean): Promise<Array<any>> {
    const result = await ProductStorageHandler.getAllMetadataWithSource(usage, suppressToast);
    return result.data;
  }

  static async updateProductMetaDataWithResponse(
    catalogId: string,
    metadata: any,
  ): Promise<AxiosResponse<any, any> | null> {
    try {
      metadata = { ...metadata };
      delete metadata.ImageExists;
      delete metadata.lastModified;
      delete metadata.ContentLength;
      delete metadata.contentLength;

      if (metadata.ApplicationVersion != "") {
        metadata = {
          ...metadata,
          Id: ProductStorageHandler.getApplicationIdFromCatalogId(metadata.CatalogId),
        };
      }
      const upload = await axios.post(`/api/updateProductMetaData/${cleanEncode(catalogId)}`, {
        metadata: JSON.stringify(metadata),
      });
      const success = upload.status == 200 && upload.data?.STATUS == "SUCCESS";
      if (success) {
        await ProductStorageHandler.upsertCachedAllMetadata(metadata);
        ProductStorageHandler.markAllMetadataCacheStale();
      }
      return upload;
    } catch (ex) {
      logger.error(ex);
      return null;
    }
  }

  static async updateProductMetaData(catalogId: string, metadata: any): Promise<boolean> {
    try {
      const upload = await ProductStorageHandler.updateProductMetaDataWithResponse(catalogId, metadata);
      const success = upload != null && upload.status == 200 && upload.data?.STATUS == "SUCCESS";
      return success;
    } catch (ex) {
      logger.error(ex);
      return false;
    }
  }

  static async getProductMetaData(catalogId: string): Promise<any> {
    if (universalstorageHandler.temporaryOffline || !navigator.onLine) {
      logger.error("missing loading of metadata from localStorage");
      return undefined;
    }

    try {
      const response = await axios.get(`/api/getProductMetaData/${cleanEncode(catalogId)}`, {
        headers: {
          "Cache-Control": "no-cache", // no itx
        },
      });
      if (response.status == 200) {
        if (response.data.STATUS !== "SUCCESS") {
          return undefined;
        }

        const data = response.data.DATA;
        if (data) return ProductStorageHandler.getCamelCasedProductInfo(data);
      }
    } catch (ex) {
      logger.error(ex);
      return undefined;
    }
  }

  private static normalizeMetadata(metadata: any): any {
    if (!metadata) return metadata;
    const priorityValue = metadata.Priority ?? metadata.priority;
    const { priority: _legacyPriority, ...metadataWithoutLegacyPriority } = metadata;
    const normalizedPriority =
      priorityValue === undefined || priorityValue === null ? "Unset" : String(priorityValue).trim();
    return {
      ...metadataWithoutLegacyPriority,
      OrderNumber: metadata.OrderNumber ? cleanDecode(metadata.OrderNumber) : metadata.OrderNumber,
      Priority: PRODUCT_PRIORITY_TYPES.includes(normalizedPriority) ? normalizedPriority : "Unset",
      ManufacturerName: metadata.ManufacturerName ? cleanDecode(metadata.ManufacturerName) : metadata.ManufacturerName,
    };
  }

  static getCamelCasedProductInfo(data: any) {
    //
    // convert all lowercase fieldnames to orignal names
    //
    let productInfo = new KnxProductInfo();
    for (let camelcaseName in productInfo) {
      const d = data[camelcaseName.toLowerCase()] ?? data[camelcaseName];
      (productInfo as any)[camelcaseName] = d;
    }
    if (productInfo.Priority === undefined) {
      productInfo.Priority = "Unset";
    }
    if (productInfo.OrderNumber) {
      productInfo.OrderNumber = cleanDecode(productInfo.OrderNumber);
    }
    if (productInfo.ManufacturerName) {
      productInfo.ManufacturerName = cleanDecode(productInfo.ManufacturerName);
    }
    return productInfo;
  }

  static async convertBlobToBase64(blob: any, callback: any) {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      if (typeof callback === "function") {
        callback(event.target.result);
      }
    };
    reader.readAsDataURL(blob);
  }

  static getApplicationIdFromCatalogId(catalogId: string): string {
    let applicationId = "";
    // Find the indices of "_H", "HP", and "_CI" (Hardware, HardwareProgram and OrderId)
    const startIndexH = catalogId.indexOf("_H");
    const startIndexHP = catalogId.indexOf("HP") + 3; // Adding 3 to exclude "HP"
    const endIndexCI = catalogId.indexOf("_CI");

    if (startIndexH != -1 && startIndexHP != -1 && endIndexCI != -1 && startIndexHP < endIndexCI) {
      // Extract the substring before "_H"
      const manufacturerId = catalogId.substring(0, startIndexH);
      applicationId = manufacturerId + "_A-" + catalogId.substring(startIndexHP, endIndexCI);
    }
    return applicationId;
  }

  static getProductIdFromCatalogId(catalogId: string): string {
    let productId = "";
    let startIndexHP = catalogId.indexOf("_HP");
    let startIndexCI = catalogId.indexOf("_CI-") + 4; // Adding 4 to exclude "CI"

    if (startIndexHP != -1 && startIndexCI != -1 && startIndexHP < startIndexCI) {
      productId =
        catalogId.substring(0, startIndexHP) + "_P-" + catalogId.substring(startIndexCI, catalogId.length - 2);
    }
    return productId;
  }

  static getManufacturerIdFromCatalogId(catalogId: string): string {
    let manufacturerId = "";
    let startIndexH = catalogId.indexOf("_H");
    if (startIndexH != -1) {
      manufacturerId = catalogId.substring(0, startIndexH);
    }
    return manufacturerId;
  }

  static getFingerPrintFromCatalogId(catalogId: string): string {
    let fingerprint = "";
    const startIndexHP = catalogId.indexOf("HP") + 3;
    const endIndexCI = catalogId.indexOf("_CI");

    if (startIndexHP != -1 && endIndexCI != -1 && startIndexHP < endIndexCI) {
      const application = catalogId.substring(startIndexHP, endIndexCI);
      fingerprint = application.split("-").length > 2 ? application.split("-")[2] : "";
    }
    return fingerprint;
  }

  static decodeZipFile() {}

  static async convertToKnxprod(file: File) {
    if (!file) return null;

    const formData = new FormData();
    formData.set("files", file); // no itx
    // Array.from(fileList).forEach((file) => {
    //   formData.set("files", file);
    // });

    try {
      const response = await axios.post(`/api/convertToKnxprod`, formData, {
        responseType: "blob", // Important for binary data
      });

      // Get original file name
      const filename = file.name.replace(/\.vd\d$/, ".knxprod");
      // Wrap as File

      // download file here

      const convertedFile = new File([response.data], filename, {
        type: "application/octet-stream",
      });

      return [convertedFile];
    } catch (err) {
      console.error("Conversion Service Error", err);
      return null;
    }
  }
}
